import { z } from 'zod';

const DESTINATION_CONTEXT_SCHEMA = z.object({
  repositoryID: z.string().uuid(),
});

const CONNECTOR_CONTEXT_SCHEMA = z.object({
  namespace: z.string(),
  collection: z.string(),
});

const INGESTION_CONTEXT_SCHEMA = z.object({
  triggerName: z.string(),
  destinationContext: DESTINATION_CONTEXT_SCHEMA,
  connectorContext: CONNECTOR_CONTEXT_SCHEMA.nullable(),
});

export type DataPlatformUploadRequest = {
  sourceData: File;
  name?: string;
  metadata?: string;
  ingestionContext?: string;
};

export const UPLOAD_CONFIG_SCHEMA = z.object({
  upload_url: z.url(),
  upload_ingestion_context: INGESTION_CONTEXT_SCHEMA,
  status_base_url: z.url(),
});
export type UploadConfigResponse = z.infer<typeof UPLOAD_CONFIG_SCHEMA>;

export const DATA_PLATFORM_UPLOAD_RESPONSE_SCHEMA = z.object({
  fileId: z.string().uuid(),
  stageId: z.string().uuid(),
  createdAt: z.string(),
  updatedAt: z.string(),
  name: z.string(),
  version: z.string().uuid(),
  mediaType: z.string(),
  size: z.number(),
});
export type DataPlatformUploadResponse = z.infer<typeof DATA_PLATFORM_UPLOAD_RESPONSE_SCHEMA>;

const DATA_PLATFORM_POLLING_STATUS_SCHEMA = z.union([
  z.literal('created'),
  z.literal('running'),
  z.literal('completed'),
  z.literal('failed'),
]);
const DATA_PLATFORM_ERROR_CODES = z.union([
  z.literal('DATA0001'),
  z.literal('DATA0002'),
  z.literal('DATA0003'),
  z.literal('DATA0004'),
  z.literal('DATA0005'),
  z.literal('DATA0006'),
  z.literal('DATA0007'),
  z.literal('DATA0008'),
  z.literal('DATA0009'),
]);

const RUN_METADATA_SCHEMA = z.object({
  fileName: z.string().optional(),
  name: z.string().optional(),
  dataSource: z.string().optional(),
  createdAt: z.string().optional(),
  documentType: z.string().optional(),
  type: z.string().optional(),
  fileSize: z.number().optional(),
  size: z.number().optional(),
});

const DATA_PLATFORM_RUNS_SCHEMA = z.object({
  status: DATA_PLATFORM_POLLING_STATUS_SCHEMA,
  output: z
    .object({
      datasetId: z.string(),
    })
    .nullable(),
  errors: z
    .array(
      z.object({
        type: z.string(),
        message: z.string(),
        httpCode: z.number(),
        dataCode: DATA_PLATFORM_ERROR_CODES,
      })
    )
    .nullable(),
  metadata: RUN_METADATA_SCHEMA.nullable().optional(),
});
export type DataPlatformPollingRuns = z.infer<typeof DATA_PLATFORM_RUNS_SCHEMA>;
export const DATA_PLATFORM_POLLING_RESPONSE_SCHEMA = z.object({
  page: z.number(),
  size: z.number(),
  total: z.number(),
  runs: z.array(DATA_PLATFORM_RUNS_SCHEMA),
});
export type DataPlatformPollingResponse = z.infer<typeof DATA_PLATFORM_POLLING_RESPONSE_SCHEMA>;
export enum UploadStatus {
  IDLE = 'idle',
  UPLOADING = 'uploading',
  UPLOADING_FAILED = 'uploading-failed',
  UPLOADED = 'uploaded',
  PROCESSING = 'processing',
  PROCESSING_FAILED = 'processing-failed',
  PROCESSED = 'processed',
  ERROR = 'error',
  SUCCESS = 'success',
}
